// 用户注册页面 - 现代简约风格
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { UserPlus, Mail, Lock, User, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const { referralCode } = useParams<{ referralCode?: string }>() // 从URL获取推荐码
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState(referralCode || ''); // 推荐码输入框
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(true); // 默认选中
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'client' | 'server' | ''>('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const navigate = useNavigate();



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 客户端验证
    if (!username.trim()) {
      setError(t('pleaseEnterUsername'));
      setErrorType('client');
      return;
    }
    if (username.length < 3) {
      setError(t('usernameTooShort'));
      setErrorType('client');
      return;
    }
    if (!email.trim()) {
      setError(t('pleaseEnterEmail'));
      setErrorType('client');
      return;
    }
    if (!email.includes('@') || !email.includes('.')) {
      setError(t('pleaseEnterValidEmail'));
      setErrorType('client');
      return;
    }
    if (!password.trim()) {
      setError(t('pleaseEnterPassword'));
      setErrorType('client');
      return;
    }
    if (!confirmPassword.trim()) {
      setError(t('pleaseConfirmPassword'));
      setErrorType('client');
      return;
    }
    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatchError'));
      setErrorType('client');
      return;
    }
    if (password.length < 6) {
      setError(t('passwordTooShort'));
      setErrorType('client');
      return;
    }

    if (!agreeToTerms) {
      setError(t('mustAgreeToTerms'));
      setErrorType('client');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/register', {
        email,
        password,
        username,
        referralCode: referralCodeInput.trim() || undefined, // 包含推荐码
      });

      // 注册成功，显示邮箱验证提示
      setShowEmailVerificationModal(true);
    } catch (err: any) {
      let errorMessage = t('somethingWentWrong');

      if (err.response) {
        const status = err.response.status;
        const serverMessage = err.response?.data?.error?.message || err.response?.data?.message;
        const errorType = err.response?.data?.error?.type;

        console.log('Registration error details:', { 
          status, 
          serverMessage, 
          errorType, 
          fullResponse: err.response.data,
          fullError: err.response
        });
        console.log('Complete error object:', JSON.stringify(err.response, null, 2));

        switch (status) {
          case 400:
            if (errorType === 'validation_error') {
              errorMessage = serverMessage || t('checkRegistrationInfo');
            } else if (errorType === 'invalid_referral_code') {
              errorMessage = t('invalidReferralCode');
            } else if (errorType === 'referral_limit_exceeded') {
              errorMessage = t('referralLimitExceeded');
            } else {
              errorMessage = serverMessage || t('invalidRegistrationInfo');
            }
            break;
          case 409:
            if (errorType === 'auth_error' && serverMessage?.toLowerCase().includes('email already registered')) {
              errorMessage = t('emailAlreadyRegistered');
            } else {
              errorMessage = t('emailAlreadyRegisteredAlt');
            }
            break;
          case 422:
            if (serverMessage?.toLowerCase().includes('password')) {
              errorMessage = t('passwordTooWeak');
            } else if (serverMessage?.toLowerCase().includes('email')) {
              errorMessage = t('invalidEmailFormat');
            } else {
              errorMessage = serverMessage || t('invalidEmailOrPassword');
            }
            break;
          case 429:
            errorMessage = t('tooManyAttempts');
            break;
          case 500:
            if (errorType === 'auth_error') {
              errorMessage = t('registrationServiceError');
            } else if (errorType === 'referral_error') {
              errorMessage = t('referralError');
            } else {
              errorMessage = t('serverError');
            }
            break;
          case 503:
            errorMessage = t('serviceUnavailable');
            break;
          default:
            errorMessage = serverMessage || `Registration failed with error ${status}. Please try again or contact support.`;
        }
      } else if (err.request) {
        errorMessage = t('networkFailed');
      } else {
        errorMessage = t('registrationRequestFailed');
      }

      setError(errorMessage);
      setErrorType('server');
      
      // 添加调试信息
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailVerificationConfirm = () => {
    setShowEmailVerificationModal(false);
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex">
      {/* 左侧操作区域 */}
      <div className="w-1/2 bg-white flex items-center justify-center px-8 lg:px-16">
        <div className="w-full max-w-md">
          {/* Logo和标题 */}
          <div className="mb-8">
            <div className="flex items-center justify-center lg:justify-start mb-4">
              <img src={`${process.env.PUBLIC_URL}/mplogo.png`} alt="ModelPool" className="h-10 w-10 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">ModelPool</h1>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('joinModelPool')}</h2>
            <p className="text-gray-600">{t('createAccountAndStart')}</p>
          </div>

          {/* 注册表单 */}
          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center space-x-2">
                <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* 用户名输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{t('username')}</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                placeholder={t('chooseUsername')}
              />
            </div>

            {/* 邮箱输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{t('emailAddress')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error && errorType === 'client') {
                    setError('');
                    setErrorType('');
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                placeholder={t('enterEmail')}
              />
            </div>

            {/* 密码输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{t('password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error && errorType === 'client') {
                      setError('');
                      setErrorType('');
                    }
                  }}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                  placeholder={t('createPassword')}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-gray-500 text-xs">{t('minimumCharacters')}</p>
            </div>

            {/* 确认密码输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">{t('confirmPassword')}</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error && errorType === 'client') {
                      setError('');
                      setErrorType('');
                    }
                  }}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                  placeholder={t('confirmYourPassword')}
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition duration-200"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-red-500 text-xs">{t('passwordsDoNotMatch')}</p>
              )}
            </div>

            {/* 推荐码输入 */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {t('referralCode')} <span className="text-gray-500 font-normal">{t('optional')}</span>
              </label>
              <input
                type="text"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900 placeholder-gray-500"
                placeholder={t('enterReferralCode')}
                maxLength={8}
              />
              {referralCode && (
                <p className="text-green-600 text-xs flex items-center space-x-1">
                  <span>{t('referralCodeApplied')}</span>
                </p>
              )}
            </div>

            {/* 同意条款复选框 */}
            <div className="flex items-start space-x-3">
              <input
                id="agree-terms"
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-0.5"
              />
              <label htmlFor="agree-terms" className="text-sm text-gray-700 leading-relaxed">
                {t('agreeToTerms')}{' '}
                <a href="/terms" target="_blank" className="text-blue-600 hover:text-blue-700 underline font-medium">
                  {t('termsOfService')}
                </a>{' '}
                {t('and')}{' '}
                <a href="/privacy" target="_blank" className="text-blue-600 hover:text-blue-700 underline font-medium">
                  {t('privacyPolicy')}
                </a>
              </label>
            </div>

            {/* 注册按钮 */}
            <button
              type="submit"
              disabled={loading || !agreeToTerms}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="spinner"></div>
                  <span>{t('creatingAccount')}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <UserPlus className="h-5 w-5" />
                  <span>{t('createAccount')}</span>
                </div>
              )}
            </button>

            {/* 登录链接 */}
            <div className="text-center">
              <span className="text-sm text-gray-600">{t('alreadyHaveAccount')} </span>
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                {t('signIn')}
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* 右侧营销区域 */}
      <div className="w-1/2 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex items-center justify-center px-8 lg:px-16 relative">
        <div className="absolute top-6 right-6">
          <LanguageSelector />
        </div>
        <div className="text-center text-white max-w-lg">
          <h2 className="text-4xl font-bold mb-6 leading-tight">
            {t('startYourAI')}
            <span className="block bg-gradient-to-r from-yellow-300 to-pink-300 bg-clip-text text-transparent">
              {t('aiJourneyToday')}
            </span>
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold mb-3">{t('instantAccess')}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {t('instantAccessDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold mb-3">{t('welcomeBonus')}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {t('welcomeBonusDesc')}
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold mb-3">{t('earnWhileGrow')}</h3>
              <p className="text-white/90 text-sm leading-relaxed">
                {t('earnWhileGrowDesc')}
              </p>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10">
            <p className="text-white/80 text-xs italic">
              "{t('testimonialRegister')}"
            </p>
            <p className="text-white/60 text-xs mt-2">{t('techStartupFounder')}</p>
          </div>
        </div>
      </div>
      {/* 邮箱验证提示弹窗 */}
      {showEmailVerificationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('checkYourEmail')}</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t('verificationEmailSent')} <span className="font-semibold text-blue-600">{email}</span>.
                {t('checkInboxAndClick')}
              </p>
              <button
                onClick={handleEmailVerificationConfirm}
                className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
              >
                {t('goToLogin')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default Register;